import React, { useState, useEffect, useRef } from "react";
import { categoryMeta, courseCategories } from "../../data/navigation";


const TopMenuWithDropdown = () => {
  const [isCoursesOpen, setIsCoursesOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const dropdownRef = useRef(null);

  // Update `isMobile` state on window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close on outside click (mobile)
  useEffect(() => {
    if (!isMobile || !isCoursesOpen) return;
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsCoursesOpen(false);
        setSelectedCategory(null);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isMobile, isCoursesOpen]);

  return (
    <div ref={dropdownRef}>
      {/* Top Menu */}
      <div className="top-menu">
        <div
          className={`menu-item ddm_trigger ${isCoursesOpen ? "ddm_active" : ""}`}
          onMouseEnter={() => {
            if (!isMobile) {
              setIsCoursesOpen(true);
              setSelectedCategory("Popular Live Course");
            }
          }}
          onMouseLeave={() => {
            if (!isMobile) {
              setIsCoursesOpen(false);
            }
          }}
          onClick={() => {
            if (isMobile) {
              setIsCoursesOpen((prev) => !prev);
              if (!isCoursesOpen) setSelectedCategory(null);
            }
          }}
        >
          COURSES <span className={`ddm_arrow ${isCoursesOpen ? "ddm_arrow_open" : ""}`}>&#9662;</span>
        </div>
      </div>

      {/* Dropdown Menu */}
      {isCoursesOpen && (
        <>
          {/* Mobile Dropdown */}
          {isMobile && (
            <div className="ddm_overlay">
              <div className="ddm_mobile">
                {/* Header */}
                <div className="ddm_mob_header">
                  <button
                    className="ddm_mob_close"
                    onClick={() => { setIsCoursesOpen(false); setSelectedCategory(null); }}
                  >
                    <i className="fa fa-times"></i>
                  </button>
                  <h3 className="ddm_mob_title">
                    {selectedCategory ? selectedCategory : "Explore Courses"}
                  </h3>
                  {selectedCategory && (
                    <button
                      className="ddm_mob_back"
                      onClick={() => setSelectedCategory(null)}
                    >
                      <i className="fa fa-arrow-left"></i> Back
                    </button>
                  )}
                </div>

                {/* Category List */}
                {!selectedCategory && (
                  <div className="ddm_mob_categories">
                    {Object.keys(courseCategories).map((category, idx) => (
                      <div
                        key={category}
                        className="ddm_mob_cat_item"
                        onClick={() => setSelectedCategory(category)}
                        style={{ animationDelay: `${idx * 0.08}s` }}
                      >
                        <div className="ddm_mob_cat_icon" style={{ background: categoryMeta[category]?.color || "#7c3aed" }}>
                          <i className={`fa ${categoryMeta[category]?.icon || "fa-book"}`}></i>
                        </div>
                        <div className="ddm_mob_cat_info">
                          <span className="ddm_mob_cat_name">{category}</span>
                          <span className="ddm_mob_cat_count">{courseCategories[category].length} courses</span>
                        </div>
                        <i className="fa fa-chevron-right ddm_mob_cat_arrow"></i>
                      </div>
                    ))}
                  </div>
                )}

                {/* Course List */}
                {selectedCategory && (
                  <div className="ddm_mob_courses">
                    {courseCategories[selectedCategory].map((course, index) => (
                      <a
                        key={index}
                        href={course.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ddm_mob_course_card"
                        style={{ animationDelay: `${index * 0.06}s` }}
                      >
                        <div className="ddm_mob_course_img_wrap">
                          <img src={course.image} alt={course.name} />
                          {course.badge && <span className="ddm_badge">{course.badge}</span>}
                        </div>
                        <div className="ddm_mob_course_info">
                          <h4>{course.name}</h4>
                          <p>{course.description}</p>
                        </div>
                        <i className="fa fa-arrow-right ddm_mob_course_arrow"></i>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Desktop Dropdown */}
          {!isMobile && (
            <div
              className="ddm_desktop"
              onMouseEnter={() => setIsCoursesOpen(true)}
              onMouseLeave={() => setIsCoursesOpen(false)}
            >
              <div className="ddm_glow_bar"></div>
              {/* Left Panel */}
              <div className="ddm_left">
                <div className="ddm_left_header">
                  <span className="ddm_left_label">Explore</span>
                </div>
                {Object.keys(courseCategories).map((category, idx) => (
                  <div
                    key={category}
                    className={`ddm_cat_item ${selectedCategory === category ? "ddm_cat_active" : ""}`}
                    onMouseEnter={() => setSelectedCategory(category)}
                    style={{ animationDelay: `${idx * 0.05}s` }}
                  >
                    <div className="ddm_cat_icon" style={{ background: selectedCategory === category ? (categoryMeta[category]?.color || "#7c3aed") : "transparent", borderColor: categoryMeta[category]?.color || "#7c3aed" }}>
                      <i className={`fa ${categoryMeta[category]?.icon || "fa-book"}`} style={{ color: selectedCategory === category ? "#fff" : (categoryMeta[category]?.color || "#7c3aed") }}></i>
                    </div>
                    <div className="ddm_cat_text">
                      <span className="ddm_cat_name">{category}</span>
                      <span className="ddm_cat_count">{courseCategories[category].length} items</span>
                    </div>
                    {selectedCategory === category && <div className="ddm_cat_indicator"></div>}
                  </div>
                ))}
              </div>

              {/* Right Panel */}
              <div className="ddm_right">
                <div className="ddm_right_header">
                  <h3 className="ddm_right_title">{selectedCategory}</h3>
                  <span className="ddm_right_count">{courseCategories[selectedCategory]?.length} courses</span>
                </div>
                <div className="ddm_right_grid">
                  {courseCategories[selectedCategory]?.map((course, index) => (
                    <a
                      key={index}
                      href={course.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ddm_course_card"
                      style={{ animationDelay: `${index * 0.07}s` }}
                    >
                      <div className="ddm_course_accent"></div>
                      <div className="ddm_course_img_wrap">
                        <img src={course.image} alt={course.name} />
                        {course.badge && <span className="ddm_badge">{course.badge}</span>}
                      </div>
                      <div className="ddm_course_info">
                        <h4>{course.name}</h4>
                        <p>{course.description}</p>
                      </div>
                      <div className="ddm_course_link">
                        <i className="fa fa-arrow-right"></i>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TopMenuWithDropdown;
